// usage.ts — the business rules behind the rail's usage + upgrade block (handoff README §5
// "The usage + upgrade block" / Shell Anatomy.dc.html §04 "The escalation ladder"; task-6-brief
// Step 1–3). Pure and tested, on purpose: `usageTier`'s `cap === null` branch is exactly where
// this product already shipped a visible bug — `BillingSummaryDto.plan.caps` fields are
// `number | null` (`null` meaning *no cap*, per the DTO's own doc comment: internal workspaces
// serialize `Infinity` to `null` over the wire), and `null <= 0` is `true` in JS, so a naive
// zero-cap guard fired a "100% of monthly messages" banner at an unlimited workspace in
// August 2026. Keeping the tier math, the date math, and the CTA copy here — rather than
// scattered through `UsageBlock.tsx`'s JSX — means every branch in this file is exercised by
// `usage.test.ts` without a browser.
import type { BillingSummaryDto, PlanCode } from '@repo/shared-types';
import { PLAN_CODES, PLAN_LIMITS } from '@repo/shared-types';
import type { Catalog } from '../locales/en';
import type { Locale } from '../locales/index';
import { formatNumber } from '../locales/format';

export type UsageTier = 'uncapped' | 'quiet' | 'warn' | 'critical' | 'paused';

/** Which rung of the escalation ladder a usage figure sits on (handoff README §5). Integer
 * comparisons throughout — `cap * 1.1` is not exact in IEEE 754 (`100 * 1.1 ===
 * 110.00000000000001`), which would make the 110% boundary one message stricter than the plan
 * promises. The API's own over-capacity gate already uses this same `x * 10 >= cap * 11` form. */
export function usageTier(used: number, cap: number | null): UsageTier {
  if (cap === null || cap <= 0) return 'uncapped';
  if (used * 10 >= cap * 11) return 'paused';
  if (used * 10 >= cap * 9) return 'critical';
  if (used * 10 >= cap * 8) return 'warn';
  return 'quiet';
}

/** The pages sub-row appears from 70% and only from 70% (handoff README §5) — independent of
 * the message tier above; a content-heavy site can hit its pages cap long before its message
 * cap. Same `null`/`<= 0` ⇒ no-cap guard as `usageTier`, same integer-comparison reasoning. */
export function showPagesRow(pagesUsed: number, pagesCap: number | null): boolean {
  if (pagesCap === null || pagesCap <= 0) return false;
  return pagesUsed * 10 >= pagesCap * 7;
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "3 Sep" — day-of-month + short month, UTC, no year. Matches the handoff's exact copy
 * ("Renews 3 Sep" / "Resets 1 Sep"). UTC because the usage counters themselves run on the UTC
 * calendar month (`BillingSummaryDto.usage.messagesThisMonth`'s own doc comment, mirrored by
 * `UsageWarningBanner`) — the reset date has to read on the same clock the counter resets on. */
export function formatShortDate(date: Date): string {
  return `${date.getUTCDate()} ${SHORT_MONTHS[date.getUTCMonth()]}`;
}

/** The Free plan has no `renewsAt` (DTO doc comment: `null` on the free plan — there is no
 * subscription to renew). Its "Resets …" note is the first day of the next UTC calendar month,
 * the same clock `usage.messagesThisMonth` itself resets on. Takes `now` as a parameter (rather
 * than reading `Date.now()` internally) so it stays a pure, testable function — the caller
 * supplies "now" once, by default from `usageBlockView`'s own default parameter. */
export function nextUsageResetDate(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/** The plan one rung above `key`, or `null` at the top of the ladder (`growth` today). Mirrors
 * `Billing.tsx`'s local `nextPlan` helper (same `PLAN_CODES`-index-plus-one shape) — duplicated
 * rather than imported because `Billing.tsx` doesn't export it and this task's brief scopes
 * changes to `usage.ts` / `UsageBlock.tsx` / `Rail.tsx` only. `priceUsd` is included so callers
 * never have to re-index `PLAN_LIMITS` just to build a "$29/mo" string. */
export function nextPlan(key: PlanCode): { key: PlanCode; name: string; priceUsd: number } | null {
  const next = PLAN_CODES[PLAN_CODES.indexOf(key) + 1];
  return next ? { key: next, name: PLAN_LIMITS[next].name, priceUsd: PLAN_LIMITS[next].priceUsd } : null;
}

export interface UsageBlockBar {
  /** 0–100, already clamped — safe to use directly as a CSS `width` percentage. */
  pct: number;
  /** A `var(--…)` colour reference — never a raw hex/rgb literal (no new design tokens). */
  color: string;
  /** "1,043 / 3,000" — the DTO's raw counts, comma-formatted; never a percentage in text. */
  label: string;
}

export interface UsageBlockCta {
  /** The green-checkmark line above the button. Present on every Free-plan CTA (making the
   * "Powered by Embent" badge optional is the headline benefit — see the `badgeBenefit` branch
   * below; post-badge-opt-in-split, upgrading makes it optional, it does not remove it) and on a
   * paid plan's `critical`/`paused` CTA; absent nowhere a CTA itself is absent. */
  benefit: string;
  /** "Upgrade — $79/mo" pre-critical, "Resume answers — $79/mo" once `paused` — the verb change
   * the escalation ladder calls for at ≥110%, applied uniformly regardless of which plan. */
  label: string;
}

export interface UsageBlockView {
  planName: string;
  tier: UsageTier;
  /** `null` exactly when `tier === 'uncapped'` — no bar, no percentage, nothing to divide by. */
  bar: UsageBlockBar | null;
  /** Card border colour — plain `--border` until `critical`/`paused` tint it. */
  borderColor: string;
  /** `null` only when uncapped and there's genuinely no renewal date to show (no subscription,
   * internal workspace). Otherwise always one line: reset/renewal date, or — from `critical`
   * — a remaining **count**, never a percentage (`UsageWarningBanner` already owns that number). */
  note: string | null;
  noteColor: string;
  /** `null` below 70% pages usage, or when there's no bar at all (`showPagesRow` short-circuits
   * the same way on an uncapped pages field). */
  pages: UsageBlockBar | null;
  /** `null` for the "no CTA at all" states: a paid plan under `critical`, or the top plan
   * (`growth`) with nowhere left to upgrade to. */
  cta: UsageBlockCta | null;
}

/**
 * All the derived facts `UsageBlock.tsx` renders, computed once so the component stays pure
 * presentation. `now` defaults to the real clock but is a parameter so the Free plan's "Resets …"
 * date is testable without mocking global time.
 *
 * `t: Catalog` and `locale: Locale` (locale-dashboard plan, Task 4, both required): every literal
 * moved to a `t.shell.*` key, and the comma-formatted counts (`formatNumber`, `../locales/
 * format.ts`) now use the ACTIVE app locale instead of the `'en-US'` this file used to hardcode
 * via its own local `fmt` helper — the caller (`UsageBlock.tsx`) passes both from `useLocale()`.
 */
export function usageBlockView(
  summary: BillingSummaryDto,
  t: Catalog,
  locale: Locale,
  now: Date = new Date(),
): UsageBlockView {
  const { plan, usage, internal, renewsAt } = summary;
  const msgCap = plan.caps.msgPerMonth;
  const tier = internal ? 'uncapped' : usageTier(usage.messagesThisMonth, msgCap);

  if (tier === 'uncapped') {
    return {
      planName: plan.name,
      tier,
      bar: null,
      borderColor: 'var(--border)',
      note: renewsAt ? t.shell.renewsOn({ date: formatShortDate(new Date(renewsAt)) }) : null,
      noteColor: 'var(--muted-foreground)',
      pages: null,
      cta: null,
    };
  }

  // `tier !== 'uncapped'` above already ruled out `cap === null || cap <= 0` — safe to treat as
  // a plain positive number from here on.
  const cap = msgCap as number;
  const used = usage.messagesThisMonth;
  const pct = Math.min(100, (used / cap) * 100);

  const barColor = tier === 'paused' ? 'var(--st-error)' : tier === 'critical' ? 'var(--st-paused)' : 'var(--primary)';
  const borderColor =
    tier === 'paused'
      ? 'color-mix(in oklch, var(--st-error) 45%, var(--border))'
      : tier === 'critical'
        ? 'color-mix(in oklch, var(--st-paused) 45%, var(--border))'
        : 'var(--border)';
  const noteColor = tier === 'paused' ? 'var(--st-error)' : tier === 'critical' ? 'var(--st-paused)' : 'var(--muted-foreground)';

  let note: string;
  if (tier === 'paused') {
    note = t.shell.answersPaused;
  } else if (tier === 'critical') {
    // A remaining count, never a percentage — the existing UsageWarningBanner already owns the
    // percentage from 80% onward; restating it here would say the same sentence twice.
    note = t.shell.messagesLeft({ n: formatNumber(Math.max(0, cap - used), locale) });
  } else if (plan.key === 'free') {
    note = t.shell.resetsOn({ date: formatShortDate(nextUsageResetDate(now)) });
  } else {
    note = renewsAt
      ? t.shell.renewsOn({ date: formatShortDate(new Date(renewsAt)) })
      : t.shell.resetsOn({ date: formatShortDate(nextUsageResetDate(now)) });
  }

  const pagesCap = plan.caps.pages;
  const pagesUsed = usage.pagesTotal;
  let pages: UsageBlockBar | null = null;
  if (showPagesRow(pagesUsed, pagesCap)) {
    const pCap = pagesCap as number;
    pages = {
      pct: Math.min(100, (pagesUsed / pCap) * 100),
      color: pagesUsed * 10 >= pCap * 9 ? 'var(--st-paused)' : 'var(--primary)',
      label: `${formatNumber(pagesUsed, locale)} / ${formatNumber(pCap, locale)}`,
    };
  }

  const next = nextPlan(plan.key);
  let cta: UsageBlockCta | null = null;
  if (next) {
    if (plan.key === 'free') {
      // The headline benefit on Free is fixed regardless of tier — the nearest competitor
      // charges $1,188/year for the same badge control, and Free is the only audience it
      // converts (handoff README §5 "Where does 'no Embent badge' belong?"). Only the verb
      // changes, at the same ≥110% boundary every other tier's CTA changes verb at.
      //
      // This is a THIRD site that states the badge plan fact in prose (besides
      // apps/web/app/routes/pricing.tsx and apps/dashboard/src/pages/Billing.tsx — see
      // CLAUDE.md "Pricing is rendered in THREE places"), so like those two it derives from
      // `PLAN_LIMITS[next.key].badgeRequired` rather than hand-writing what upgrading does.
      // Post-badge-opt-in-split (2026-08-11), upgrading doesn't REMOVE the badge — Free's is
      // required, a paid plan's is optional, off by default until the owner turns it on in
      // Appearance — so "removes" would be a false promise the moment the owner reads it.
      const badgeBenefit = PLAN_LIMITS[next.key].badgeRequired
        ? t.shell.badgeStillShown({ plan: next.name })
        : t.shell.badgeOptional({ plan: next.name });
      cta = {
        benefit: badgeBenefit,
        label:
          tier === 'paused'
            ? t.shell.resumeAnswersCta({ price: next.priceUsd })
            : t.shell.upgradeCta({ price: next.priceUsd }),
      };
    } else if (tier === 'paused') {
      cta = { benefit: t.shell.resumeAnswersBenefit, label: t.shell.resumeAnswersCta({ price: next.priceUsd }) };
    } else if (tier === 'critical') {
      const nextFacts = PLAN_LIMITS[next.key];
      // Seats folded into this same benefit list (THI-110) — the natural extension of the
      // existing "adds X messages and Y bots" fact list, derived the same way, rather than a
      // new CTA of its own (seat count isn't what's running out here, message volume is).
      cta = {
        benefit: t.shell.upgradeBenefit({
          plan: next.name,
          messages: formatNumber(nextFacts.maxMessages, locale),
          bots: nextFacts.maxBots,
          seats: nextFacts.members,
        }),
        label: t.shell.upgradeCta({ price: next.priceUsd }),
      };
    }
    // quiet/warn on a paid plan: no CTA at all — three lines, per handoff README §5.
  }

  return {
    planName: plan.name,
    tier,
    bar: { pct, color: barColor, label: `${formatNumber(used, locale)} / ${formatNumber(cap, locale)}` },
    borderColor,
    note,
    noteColor,
    pages,
    cta,
  };
}
